// ABOUTME: One business in the Agents tree. The row is the business's Marketing chat (Marketing
// is the business) with its pending-approvals badge; expanded, it lists the team roles Marketing
// has handed work to (or the owner has opened), greyed unless working. A role not used yet is
// not listed.
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronRight, Megaphone, type LucideIcon } from 'lucide-react'
import { AgentAvatar } from '@/components/ui/agent-avatar'
import { roleIcon } from '@/components/team/roleIcon'
import { MarkdownText } from '@/components/chat/MarkdownContent'
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'
import { cn } from '@/lib/utils'
import type { Business } from '@/stores/useMyBusinessStore'
import type { RoleRosterStatus } from '@/api/agentRoles'
import { BusinessRowMenu } from './BusinessRowMenu'
import { readCollapsed, useBusinessAgents, writeCollapsed } from './useBusinessAgents'

interface BusinessNodeProps {
  business: Business
  isActive: boolean
  /** The Marketing thread's last message (Markdown), for the active business. */
  preview?: string
  /** Open `path` in this business, switching to it first when it isn't active. */
  onOpen: (path: string) => void
  onRename: () => void
  onDelete: () => void
}

/** "Working on <brief> · 2 queued", or "2 queued" while nothing has started. */
function workLine(role: RoleRosterStatus): string | undefined {
  const queued = role.queued ? `${role.queued} queued` : ''
  if (!role.working_on) return queued || undefined
  return queued ? `Working on ${role.working_on} · ${queued}` : `Working on ${role.working_on}`
}

interface SubRowProps {
  id: string
  icon: LucideIcon
  name: string
  detail?: string
  status?: string
  dim: boolean
  active: boolean
  onClick: () => void
  testId: string
}

function SubRow({ id, icon, name, detail, status, dim, active, onClick, testId }: SubRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
      className={cn(
        'hover:bg-sidebar-accent/60 flex min-h-11 w-full items-center gap-2.5 rounded-[10px] py-1.5 pr-2 pl-10 text-left md:min-h-9 md:pl-9',
        active && 'bg-sidebar-accent',
        dim && 'text-muted-foreground'
      )}
    >
      <AgentAvatar id={id} icon={icon} size="sm" className={cn(dim && 'opacity-60')} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm">{name}</span>
        {detail && <span className="text-muted-foreground truncate text-xs">{detail}</span>}
      </span>
      {status && (
        <span
          className={cn('shrink-0 text-xs', status === 'working' && 'text-primary font-medium')}
        >
          {status}
        </span>
      )}
    </button>
  )
}

export function BusinessNode({
  business,
  isActive,
  preview,
  onOpen,
  onRename,
  onDelete,
}: BusinessNodeProps) {
  const { pathname } = useLocation()
  const [collapsed, setCollapsed] = useState(() => readCollapsed(business.id, !isActive))
  const { pending, roster } = useBusinessAgents(business.id, !collapsed)

  const toggle = () => {
    writeCollapsed(business.id, !collapsed)
    setCollapsed(!collapsed)
  }
  const listed = roster.filter((role) => role.listed && role.role_id && role.state !== 'off')

  return (
    <div data-testid={`business-node-${business.id}`} data-active={isActive || undefined}>
      <div
        className={cn(
          'hover:bg-sidebar-accent/60 flex items-center rounded-xl',
          isActive && pathname === '/chat' && 'bg-sidebar-accent'
        )}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${business.name}`}
          data-testid={`business-toggle-${business.id}`}
          className="text-muted-foreground flex size-11 shrink-0 items-center justify-center md:size-8"
        >
          <ChevronRight className={cn('size-4 transition-transform', !collapsed && 'rotate-90')} />
        </button>
        <button
          type="button"
          onClick={() => onOpen(CHAT_THREAD_PATH)}
          aria-current={isActive && pathname === '/chat' ? 'page' : undefined}
          data-testid={`business-row-${business.id}`}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 py-2 text-left"
        >
          <AgentAvatar id={business.id} icon={Megaphone} size="sm" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[0.9375rem] font-medium">{business.name}</span>
            {preview && (
              <span className="text-muted-foreground truncate text-[0.8125rem]">
                <MarkdownText content={preview} />
              </span>
            )}
          </span>
          {pending > 0 && (
            <span
              aria-label={`${pending} waiting for you`}
              data-testid={`business-unread-${business.id}`}
              className="bg-primary text-primary-foreground inline-flex min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold"
            >
              {pending}
            </span>
          )}
        </button>
        <BusinessRowMenu
          business={business}
          onRename={onRename}
          onSettings={() => onOpen('/settings/business')}
          onDelete={onDelete}
        />
      </div>

      {!collapsed && (
        <div
          className="flex flex-col gap-0.5 pt-0.5"
          data-testid={`business-agents-${business.id}`}
        >
          {listed.map((role) => (
            <SubRow
              key={role.slug}
              id={role.role_id!}
              icon={roleIcon(role.slug)}
              name={role.name}
              detail={workLine(role)}
              status={role.state === 'working' ? role.state : undefined}
              dim={role.state !== 'working'}
              active={isActive && pathname === `/chat/${role.role_id}`}
              onClick={() => onOpen(`/chat/${role.role_id}`)}
              testId={`thread-role-${role.role_id}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
